import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

// A single QueryClient instance shared across the app.
const queryClient = new QueryClient();

interface AppProvidersProps {
  children: React.ReactNode;
  /**
   * Server-only: the mutable context object react-helmet-async fills with the
   * rendered head tags during renderToString. Omitted on the client.
   */
  helmetContext?: Record<string, unknown>;
}

/**
 * The shared provider tree used by BOTH the client entry (main.tsx) and the
 * server entry (entry-server.tsx). Keeping providers in one place guarantees
 * HelmetProvider is never duplicated — duplicate HelmetProviders break SSR head
 * extraction (each provider gets its own context and the outer one ships empty).
 *
 * NOTE: the Router is intentionally NOT included here. The client uses
 * BrowserRouter and the server uses StaticRouter, so each entry supplies its own.
 */
const AppProviders = ({ children, helmetContext }: AppProvidersProps) => (
  <HelmetProvider context={helmetContext}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {children}
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default AppProviders;
